import { useEffect, useRef, useState } from 'react';
import type { LocalPhoto } from './PhotoAttach';

interface Stroke {
  points: { x: number; y: number }[];
}

interface Props {
  photo: LocalPhoto;
  onSave: (annotated: LocalPhoto) => void;
  onCancel: () => void;
}

/**
 * Full-screen photo annotator. Loads the photo onto a canvas, lets the user
 * draw red-ink strokes on top with finger / stylus / mouse, then flattens the
 * result back into a new JPEG blob.
 */
export function PhotoAnnotator({ photo, onSave, onCancel }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [imgReady, setImgReady] = useState(false);
  const drawingRef = useRef(false);
  const currentStrokeRef = useRef<Stroke | null>(null);

  // Load the source image onto an offscreen Image element.
  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imgRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      setImgReady(true);
    };
    img.src = photo.preview;
  }, [photo.preview]);

  // Re-render whenever strokes change.
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img || !imgReady) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    ctx.strokeStyle = '#FF1F1F';
    ctx.lineWidth = Math.max(4, canvas.width / 220);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokes) {
      if (stroke.points.length === 0) continue;
      ctx.beginPath();
      ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
      if (stroke.points.length === 1) {
        // Single tap — render as a small dot
        ctx.lineTo(stroke.points[0].x + 0.5, stroke.points[0].y + 0.5);
      } else {
        for (let i = 1; i < stroke.points.length; i++) {
          ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
        }
      }
      ctx.stroke();
    }
  }, [strokes, imgReady]);

  function pointerToCanvas(e: PointerEvent): { x: number; y: number } | null {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }

  // Pointer event handlers attached to the canvas.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const start = (e: PointerEvent) => {
      const p = pointerToCanvas(e);
      if (!p) return;
      e.preventDefault();
      canvas.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      currentStrokeRef.current = { points: [p] };
      setStrokes((prev) => [...prev, currentStrokeRef.current!]);
    };

    const move = (e: PointerEvent) => {
      if (!drawingRef.current || !currentStrokeRef.current) return;
      const p = pointerToCanvas(e);
      if (!p) return;
      e.preventDefault();
      currentStrokeRef.current.points.push(p);
      setStrokes((prev) => [...prev]); // trigger re-render
    };

    const end = (e: PointerEvent) => {
      drawingRef.current = false;
      currentStrokeRef.current = null;
      try { canvas.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    };

    canvas.addEventListener('pointerdown', start);
    canvas.addEventListener('pointermove', move);
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);

    return () => {
      canvas.removeEventListener('pointerdown', start);
      canvas.removeEventListener('pointermove', move);
      canvas.removeEventListener('pointerup', end);
      canvas.removeEventListener('pointercancel', end);
    };
  }, [imgReady]);

  function undo() {
    setStrokes((prev) => prev.slice(0, -1));
  }

  function clearAll() {
    setStrokes([]);
  }

  function save() {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        URL.revokeObjectURL(photo.preview);
        onSave({ blob, preview: URL.createObjectURL(blob) });
      },
      'image/jpeg',
      0.88
    );
  }

  return (
    <div className="annotator-backdrop" role="dialog" aria-label="Annotate photo">
      <header className="annotator-header">
        <button type="button" className="annotator-action ghost" onClick={onCancel}>
          Cancel
        </button>
        <span className="annotator-title">Mark issues in red</span>
        <button type="button" className="annotator-action primary" onClick={save} disabled={strokes.length === 0}>
          Save
        </button>
      </header>

      <div className="annotator-canvas-wrap">
        {!imgReady && <p className="muted" style={{ color: '#fff' }}>Loading image…</p>}
        <canvas ref={canvasRef} className="annotator-canvas" />
      </div>

      <footer className="annotator-footer">
        <button
          type="button"
          className="annotator-action ghost"
          onClick={undo}
          disabled={strokes.length === 0}
        >
          ↶ Undo
        </button>
        <span className="annotator-pen-label">
          <span className="annotator-pen-dot" /> Red pen
        </span>
        <button
          type="button"
          className="annotator-action ghost"
          onClick={clearAll}
          disabled={strokes.length === 0}
        >
          Clear
        </button>
      </footer>
    </div>
  );
}
